/**
 * Route guards that compose on top of :class:`AuthProvider`.
 *
 * ``RequireFullSession`` / ``RequireSemiAuth`` / ``RequirePublic``
 * live in :mod:`App` for historical reasons (spec 0005). This module
 * adds ``RequireSuperAdmin`` for spec 0006's super-only routes.
 *
 * Per spec 0006 §5.8, ``RequireSuperAdmin`` does NOT redirect on
 * mismatch — it renders an inline "you need super-admin access" block
 * so a tenant operator who deep-linked from a shared URL sees *why*
 * they were stopped rather than silently bouncing to the dashboard.
 */
import { Link, Navigate } from "react-router-dom";
import { type ReactNode } from "react";
import { useAuth } from "@/lib/use-auth";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </main>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (user.role !== "super") {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
        <Alert variant="destructive">
          <AlertTitle>Super-admin access required</AlertTitle>
          <AlertDescription>
            This page is restricted to super administrators. If you think you
            should have access, ask another super admin to grant your account
            the role.
          </AlertDescription>
        </Alert>
        <div className="mt-4">
          <Link
            to="/"
            className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Back to dashboard
          </Link>
        </div>
      </main>
    );
  }
  return <>{children}</>;
}
